import { api } from './api';

export interface Service {
  _id: string;
  name: string;
  description?: string;
  shortDescription?: string;

  duration: number;
  formattedDuration: string;
  bufferTime: {
    before: number;
    after: number;
  };
  maxAdvanceBooking: number;
  minAdvanceBooking: number;

  pricingType: 'fixed' | 'hourly' | 'custom' | 'free';
  price: number;
  formattedPrice: string;
  currency: string;
  depositRequired: boolean;
  depositAmount?: number;
  depositType?: 'fixed' | 'percentage';

  category: string;
  tags: string[];
  color: string;

  meetingType: 'online' | 'in_person' | 'phone' | 'flexible';
  location?: string;
  onlineMeetingUrl?: string;
  instructions?: string;

  allowClientBooking: boolean;
  requireApproval: boolean;
  maxBookingsPerDay?: number;
  maxBookingsPerWeek?: number;

  displayOrder: number;
  status: 'active' | 'inactive' | 'archived';

  createdAt: string;
  updatedAt: string;
}

export interface ServiceCategory {
  value: string;
  label: string;
  icon: string;
}

export interface ServicesListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'active' | 'inactive' | 'archived' | 'all';
  category?: string;
  allowClientBooking?: 'true' | 'false' | 'all';
  sortBy?: 'name' | 'price' | 'duration' | 'displayOrder' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

export interface ServicesListResponse {
  services: Service[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

export interface ServiceStats {
  total: number;
  active: number;
  inactive: number;
  bookable: number;
  byCategory: { _id: string; count: number }[];
}

export type CreateServiceData = {
  name: string;
  description?: string;
  shortDescription?: string;
  duration: number;
  bufferTime?: { before: number; after: number };
  maxAdvanceBooking?: number;
  minAdvanceBooking?: number;
  pricingType: 'fixed' | 'hourly' | 'custom' | 'free';
  price: number;
  currency?: string;
  depositRequired?: boolean;
  depositAmount?: number;
  depositType?: 'fixed' | 'percentage';
  category: string;
  tags?: string[];
  color?: string;
  meetingType: 'online' | 'in_person' | 'phone' | 'flexible';
  location?: string;
  onlineMeetingUrl?: string;
  instructions?: string;
  allowClientBooking?: boolean;
  requireApproval?: boolean;
  maxBookingsPerDay?: number;
  maxBookingsPerWeek?: number;
  displayOrder?: number;
  status?: 'active' | 'inactive';
};

export type UpdateServiceData = Partial<CreateServiceData>;

// API Functions
export const servicesApi = {
  // List services
  list: async (params: ServicesListParams = {}): Promise<ServicesListResponse> => {
    const response = await api.get('/services', { params });
    return response.data.data;
  },

  // Get single service
  get: async (id: string): Promise<Service> => {
    const response = await api.get(`/services/${id}`);
    return response.data.data.service;
  },

  // Get service stats
  getStats: async (): Promise<ServiceStats> => {
    const response = await api.get('/services/stats');
    return response.data.data;
  },

  // Get categories
  getCategories: async (): Promise<ServiceCategory[]> => {
    const response = await api.get('/services/categories');
    return response.data.data.categories;
  },

  // Create service
  create: async (data: CreateServiceData): Promise<Service> => {
    const response = await api.post('/services', data);
    return response.data.data.service;
  },

  // Update service
  update: async (id: string, data: UpdateServiceData): Promise<Service> => {
    const response = await api.patch(`/services/${id}`, data);
    return response.data.data.service;
  },

  // Delete/archive service
  delete: async (id: string, permanent = false): Promise<void> => {
    await api.delete(`/services/${id}`, { params: { permanent } });
  },

  // Toggle status
  toggleStatus: async (id: string): Promise<Service> => {
    const response = await api.patch(`/services/${id}/toggle-status`);
    return response.data.data.service;
  },

  // Toggle booking visibility
  toggleBooking: async (id: string): Promise<Service> => {
    const response = await api.patch(`/services/${id}/toggle-booking`);
    return response.data.data.service;
  },

  // Reorder services
  reorder: async (serviceIds: string[]): Promise<void> => {
    await api.post('/services/reorder', { serviceIds });
  },

  // Duplicate service
  duplicate: async (id: string): Promise<Service> => {
    const response = await api.post(`/services/${id}/duplicate`);
    return response.data.data.service;
  },
};
